import type { GuestTrip } from '@mv/contracts';

/**
 * The guest's own four constants: when they land, when they leave, where they
 * are sitting.
 *
 * **The only static thing on Now.** Everything else on that page moves — the
 * countdown counts, the hero card advances through the week, the photo strip
 * fills up. These four are fixed once the seed CSV is filled and are looked up
 * rather than read: at a check-in desk, in a taxi, walking into the reception.
 * So it is a card somebody scans in one second, not prose.
 *
 * **It always shows all four, and a missing one is a dash.** This card was
 * built the other way first — absent when it had nothing, on the argument that
 * four blank labels are worse than no card. That was wrong about what the
 * blanks say. A guest who has given us no flights and sees nothing cannot tell
 * whether we are not showing the information or do not have it; a guest who
 * sees `Arrive —` knows the row exists, knows it is theirs, and knows what is
 * missing from it. The empty state is the *common* one today and will be for
 * months, and it is the one that has to communicate.
 *
 * So the emptiness check moved up to `Now`, which asks whether the guest holds
 * a roster row at all — a person with no row has no trip to be missing.
 *
 * **Three short things and one long one.** They sat in a single row once and
 * left the right of the card empty, because a date is narrow and a dash is
 * narrower. Spreading them evenly is not the fix either — that gives the table
 * a quarter of a 360px rail and wraps a 25-character name over several lines.
 * So the two dates and the room share a row and the table takes one of its own,
 * which fills the card at both widths and fits the longest table on one line.
 *
 * Values are **strings, not timestamps**, and are printed as written. They come
 * off a spreadsheet a human fills in, and parsing them into `Date` to reformat
 * them would turn a typo into an exception on the landing screen for the one
 * guest whose row is wrong. `lib/time` is for the schedule, which has real ISO
 * instants.
 */

/**
 * What stands in for a fact nobody has given us yet.
 *
 * An em dash rather than two hyphens: every other absence in this app is
 * written this way, and `--` beside a proper name reads as markup that did not
 * render. It is wrapped in an `aria-label` at the call site below, because a
 * screen reader announcing "Arrive, em dash" is not an answer.
 */
const MISSING = '—';

/**
 * The order they are wanted in — dates before the trip, table during it — and
 * how much of the card each is allowed.
 *
 * Fixed rather than derived from the object, so a field nobody has filled in
 * still holds its place and the card does not reshuffle as the spreadsheet
 * does.
 *
 * `wide` is the table, and it is the table because of what the values are, not
 * what they mean. Three of the four are short and stay short — "5 Nov", "Nov 5",
 * a room number or a villa name — so they sit across one row. A table is a
 * *name*, up to about 25 characters, and a third-width column wraps it. It takes
 * a row of its own, which fits the longest one on a single line and happens to
 * give it the emphasis it wants on the night.
 *
 * The order is the order they are wanted in, which is roughly chronological:
 * land, leave, where you sleep, where you sit.
 */
const LAYOUT: Array<{ label: string; of: (t: GuestTrip) => string | undefined; wide?: true }> = [
  { label: 'Arrive', of: (t) => t.arrival },
  { label: 'Depart', of: (t) => t.departure },
  { label: 'Room', of: (t) => t.room },
  { label: 'Table', of: (t) => t.table, wide: true },
];

export default function TripCard({ trip }: { trip: GuestTrip }) {
  return (
    <section className="card trip-card" aria-label="Your trip" data-tour="seating">
      <h5 className="trip-head">Your trip</h5>
      <dl className="trip-facts">
        {LAYOUT.map(({ label, of, wide }) => {
          const value = of(trip);
          return (
            // `dt`/`dd` rather than two spans: it is a list of term and value,
            // and a screen reader reading "Table, Washington Square Park" beats
            // the name run together with the date before it.
            <div className={wide ? 'trip-fact trip-fact-wide' : 'trip-fact'} key={label}>
              <dt>{label}</dt>
              <dd className={value ? undefined : 'trip-missing'}>
                {value ?? <span aria-label="not set yet">{MISSING}</span>}
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}
